import { Component, Suspense, lazy, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Boot } from "@/components/Boot";
import { TypeTerminal } from "@/components/TypeTerminal";
import { Panel, Marquee, Reveal, SectionHead, Tag, Dot } from "@/components/System";
import { ASSETS, STACK, PROOF_LAYERS } from "@/data/content";

const CommandCenter = lazy(() => import("@/components/three/CommandCenter"));

class SceneBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed) return this.props.fallback;
        return this.props.children;
    }
}

const SceneFallback = () => (
    <div className="relative flex h-full w-full items-center justify-center">
        <div className="vm-grid-bg absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="absolute h-64 w-64 rounded-full border border-[#00D9FF]/20 vm-spin-slow" aria-hidden="true" />
        <div className="absolute h-80 w-80 rounded-full border border-[#1688FF]/15 vm-spin-rev" aria-hidden="true" />
        <img src={ASSETS.logo} alt="AgentVM logo" className="vm-float relative h-48 w-48 object-cover shadow-[0_0_80px_rgba(0,140,255,0.35)]" />
    </div>
);

const HERO_LINES = ["PRIVATE INTELLIGENCE.", "PROVABLE ACTION."];

const Hero = () => {
    const [sceneLost, setSceneLost] = useState(false);
    return (
    <section className="relative overflow-hidden pt-16" data-testid="hero">
        <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-[60vh] w-[120vw] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(0,108,255,0.14),transparent_60%)]" aria-hidden="true" />

        <div className="relative mx-auto grid max-w-[1500px] items-center gap-8 px-4 pb-10 pt-10 sm:px-6 lg:grid-cols-[1.05fr_1.15fr_0.95fr] lg:gap-4 lg:pt-6">
            <div className="order-2 lg:order-1">
                <div className="vm-micro mb-5 flex items-center gap-2">
                    <Dot /> THE VERIFIABLE RUNTIME FOR AI AGENTS
                </div>
                <h1 className="text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl xl:text-6xl">
                    {HERO_LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${i === 1 ? "text-[#00D9FF] vm-glow" : "text-[#F5F9FF]"}`}
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{ delay: 0.25 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.7 }}
                    className="mt-6 max-w-md text-sm leading-relaxed text-[#B8C7D9] sm:text-base"
                >
                    AgentVM provides a secure execution environment for agents working with sensitive data — designed
                    around privacy, trusted execution, attestation, and verifiable evidence.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.7 }}
                    className="mt-8 flex flex-wrap gap-3"
                >
                    <Link
                        to="/terminal"
                        data-testid="cta-enter"
                        className="border border-[#1688FF]/70 bg-[#006CFF]/25 px-5 py-2.5 font-mono text-[11px] tracking-[0.24em] text-[#F5F9FF] transition-all duration-200 hover:bg-[#006CFF]/45 hover:shadow-[0_0_24px_rgba(0,108,255,0.4)]"
                    >
                        ENTER AGENTVM
                    </Link>
                    <Link
                        to="/architecture"
                        data-testid="cta-architecture"
                        className="border border-[rgba(0,217,255,0.3)] px-5 py-2.5 font-mono text-[11px] tracking-[0.24em] text-[#B8C7D9] transition-colors duration-200 hover:border-[#00D9FF]/70 hover:text-[#00D9FF]"
                    >
                        EXPLORE THE ARCHITECTURE
                    </Link>
                    <Link
                        to="/proof"
                        data-testid="cta-verify"
                        className="border border-transparent px-5 py-2.5 font-mono text-[11px] tracking-[0.24em] text-[#B8C7D9]/70 transition-colors duration-200 hover:text-[#F5F9FF]"
                    >
                        VERIFY THE SYSTEM →
                    </Link>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.05 }}
                    className="vm-micro-dim mt-10"
                >
                    PRIVATE BY DESIGN. VERIFIABLE BY DEFAULT.
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 1.1 }}
                className="order-1 h-[340px] sm:h-[420px] lg:order-2 lg:h-[600px]"
                data-testid="command-center-scene"
            >
                {sceneLost ? (
                    <SceneFallback />
                ) : (
                    <SceneBoundary fallback={<SceneFallback />}>
                        <Suspense fallback={<SceneFallback />}>
                            <CommandCenter onLost={() => setSceneLost(true)} />
                        </Suspense>
                    </SceneBoundary>
                )}
            </motion.div>

            <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.55, duration: 0.9 }}
                className="order-3"
            >
                <Panel
                    title="AGENTVM://RUNTIME"
                    right={<span className="flex items-center gap-2"><Tag tone="concept">SIMULATION</Tag></span>}
                    testid="hero-terminal"
                    className="scanlines"
                >
                    <div className="p-4 sm:p-5">
                        <TypeTerminal
                            command="boot"
                            lines={[
                                "> initializing secure runtime...",
                                "> loading agent environment...",
                                "> verifying identity...",
                                "> checking execution policy...",
                                "> connecting protected runtime...",
                                "> binding software session...",
                                "> generating evidence...",
                            ]}
                            statusRows={[
                                ["IDENTITY", "VERIFIED"],
                                ["RUNTIME", "PROTECTED"],
                                ["ATTESTATION", "VERIFIED"],
                                ["SOFTWARE BINDING", "VERIFIED"],
                                ["EVIDENCE", "READY"],
                            ]}
                            footer="SYSTEM STATUS: OPERATIONAL_"
                        />
                    </div>
                </Panel>
                <div className="mt-3 grid grid-cols-3 gap-3">
                    {[
                        ["TEE", "ACTIVE"],
                        ["ARC", "ECONOMIC LAYER"],
                        ["PROOF", "5-LAYER"],
                    ].map(([k, v]) => (
                        <div key={k} className="vm-panel-soft px-3 py-2.5">
                            <div className="vm-micro-dim">{k}</div>
                            <div className="mt-1 font-mono text-[10px] tracking-[0.14em] text-[#00D9FF]">{v}</div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>

        <div className="relative mx-auto hidden max-w-[1500px] items-center justify-between px-6 pb-6 lg:flex" aria-hidden="true">
            {["PRIVATE DATA", "AI AGENT", "TRUSTED RUNTIME", "ATTESTATION", "PROOF", "ARC", "ECONOMIC ACTION"].map((s, i, arr) => (
                <div key={s} className="flex items-center gap-3">
                    <span className="font-mono text-[9px] tracking-[0.24em] text-[#B8C7D9]/60">{s}</span>
                    {i < arr.length - 1 && <span className="text-[#00D9FF]/50">↓</span>}
                </div>
            ))}
        </div>
    </section>
    );
};

const PILLARS = [
    { to: "/runtime", code: "01", name: "PROTECTED RUNTIME", desc: "Sensitive information enters a protected execution environment. The agent operates inside it. The outside world receives proofs — not raw data.", tag: "TEE" },
    { to: "/proof", code: "02", name: "VERIFICATION CONSOLE", desc: "Five verification layers: onchain identity, evidence bundles, TDX attestation, software bindings, GPU attestation.", tag: "PROOF" },
    { to: "/economy", code: "03", name: "AGENTIC ECONOMY", desc: "When software can prove what it did, it can be trusted with economic action. Settlement happens on Arc.", tag: "ARC" },
];

const Home = () => {
    const [booted, setBooted] = useState(() => sessionStorage.getItem("avm_booted") === "1");
    const finishBoot = () => {
        sessionStorage.setItem("avm_booted", "1");
        setBooted(true);
    };

    return (
        <div>
            <Seo path="/" />
            {!booted && <Boot onDone={finishBoot} />}
            <Hero />
            <Marquee
                items={[
                    "PRIVATE INTELLIGENCE. PROVABLE ACTION.",
                    "TRUST THE RESULT. VERIFY THE RUNTIME.",
                    "PRIVATE BY DESIGN. VERIFIABLE BY DEFAULT.",
                    "AUTONOMY WITHOUT BLIND TRUST.",
                    "THE VERIFIABLE RUNTIME FOR AI AGENTS.",
                ]}
            />

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="stack-section">
                <SectionHead
                    index="SYS.01"
                    title="FROM PRIVATE DATA TO ECONOMIC ACTION."
                    sub="Every stage of the AgentVM stack exists to answer one question: how do you trust an agent without exposing everything it saw?"
                />
                <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
                    <Reveal>
                        <div className="flex flex-col">
                            {STACK.map((s, i) => (
                                <div key={s.id}>
                                    <div className="group border border-[rgba(0,217,255,0.12)] bg-[#080F18]/50 px-4 py-3 transition-colors hover:border-[#00D9FF]/35">
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-xs tracking-[0.2em] text-[#F5F9FF] sm:text-sm">{s.name}</span>
                                            <span className="font-mono text-[9px] tracking-[0.2em] text-[#00D9FF]/60">{String(i + 1).padStart(2, "0")}</span>
                                        </div>
                                        <p className="mt-1.5 text-xs leading-relaxed text-[#B8C7D9]/75">{s.desc}</p>
                                    </div>
                                    {i < STACK.length - 1 && (
                                        <div className="pl-6" aria-hidden="true">
                                            <svg width="2" height="14" className="overflow-visible">
                                                <line x1="1" y1="0" x2="1" y2="14" stroke="rgba(0,217,255,0.35)" strokeWidth="1" className="vm-dash" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <div className="flex flex-col gap-6">
                        <Reveal delay={0.1}>
                            <Panel title="AGENTVM://COMMAND_CENTER" right="VISUAL RECORD" className="scanlines overflow-hidden">
                                <img
                                    src={ASSETS.banner}
                                    alt="AgentVM command center with runtime terminal and verification displays"
                                    className="h-64 w-full object-cover object-center opacity-90 lg:h-80"
                                    loading="lazy"
                                />
                            </Panel>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div className="vm-panel-soft p-6">
                                <div className="vm-micro mb-3">CORE PHILOSOPHY</div>
                                <p className="text-xl font-semibold leading-snug text-[#F5F9FF] sm:text-2xl">
                                    PRIVATE BY DESIGN.
                                    <br />
                                    <span className="text-[#00D9FF]">VERIFIABLE BY DEFAULT.</span>
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-[#B8C7D9]">
                                    Agents can process private information, make decisions, execute workflows, and
                                    perform economic activity. AgentVM exists so that users and institutions can
                                    verify the properties of that execution — identity, environment, software, and
                                    evidence — without exposing the data itself.
                                </p>
                                <Link to="/thesis" data-testid="stack-thesis-link" className="mt-5 inline-block font-mono text-[11px] tracking-[0.22em] text-[#00D9FF] hover:underline">
                                    READ THE THESIS →
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-t border-[rgba(0,217,255,0.1)] bg-[#080F18]/40 py-24" data-testid="pillars-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <SectionHead index="SYS.02" title="THREE PLANES OF THE SYSTEM." sub="Runtime. Proof. Economy. Each plane is a complete subsystem of the AgentVM operating model." />
                    <div className="grid gap-5 md:grid-cols-3">
                        {PILLARS.map((p, i) => (
                            <Reveal key={p.code} delay={i * 0.1}>
                                <Link to={p.to} data-testid={`pillar-${p.tag.toLowerCase()}`} className="group block">
                                    <Panel title={`MODULE ${p.code}`} right={p.tag} className="h-full transition-colors duration-300 group-hover:border-[#00D9FF]/45">
                                        <div className="p-6">
                                            <h3 className="font-mono text-base tracking-[0.14em] text-[#F5F9FF] group-hover:text-[#00D9FF]">{p.name}</h3>
                                            <p className="mt-3 text-sm leading-relaxed text-[#B8C7D9]/85">{p.desc}</p>
                                            <div className="vm-micro-dim mt-6 transition-colors group-hover:text-[#00D9FF]">OPEN MODULE →</div>
                                        </div>
                                    </Panel>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="verification-strip">
                <SectionHead index="SYS.03" title="FIVE QUESTIONS. FIVE PROOFS." sub="The AgentVM verification model answers who, what, where, which code, and which hardware." />
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    {PROOF_LAYERS.map((l, i) => (
                        <Reveal key={l.id} delay={i * 0.07}>
                            <Link to="/proof" data-testid={`proof-chip-${l.id}`} className="group block border border-[rgba(0,217,255,0.12)] bg-[#080F18]/50 p-4 transition-colors hover:border-[#00D9FF]/40">
                                <div className="font-mono text-[10px] tracking-[0.2em] text-[#00D9FF]/70">{l.num}</div>
                                <div className="mt-2 font-mono text-[11px] leading-snug tracking-[0.12em] text-[#F5F9FF]">{l.name}</div>
                                <div className="vm-micro-dim mt-3">{l.question}</div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-t border-[rgba(0,217,255,0.1)] py-28" data-testid="final-message">
                <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
                    {["PRIVATE DATA.", "TRUSTED EXECUTION.", "VERIFIABLE ACTION.", "ECONOMIC AUTONOMY."].map((line, i) => (
                        <Reveal key={line} delay={i * 0.12}>
                            <div className={`text-3xl font-bold tracking-tight sm:text-5xl ${i === 3 ? "text-[#00D9FF] vm-glow" : "text-[#F5F9FF]"}`}>
                                {line}
                            </div>
                        </Reveal>
                    ))}
                    <Reveal delay={0.55}>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            <Link to="/terminal" data-testid="final-enter" className="border border-[#1688FF]/70 bg-[#006CFF]/25 px-6 py-3 font-mono text-[11px] tracking-[0.24em] text-[#F5F9FF] transition-colors hover:bg-[#006CFF]/45">
                                ENTER AGENTVM
                            </Link>
                            <Link to="/sources" data-testid="final-sources" className="border border-[rgba(0,217,255,0.3)] px-6 py-3 font-mono text-[11px] tracking-[0.24em] text-[#B8C7D9] transition-colors hover:border-[#00D9FF]/70 hover:text-[#00D9FF]">
                                VERIFY THE SOURCES
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Home;
